import { useState } from "react";
import { format } from "date-fns";
import { AlertCircle } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/ui/dialog";
import { Button } from "@/ui/button";
import { Textarea } from "@/ui/textarea";
import { ACTION } from "@/lib/auth/actions";
import { usePermission } from "@/lib/authv2/permissions";
import { formatDate } from "@/lib/format";
import { formatOccurredAt } from "@/features/branch-shift/occurredAt";
import { cn } from "@/lib/utils";
import { useMarkIncidentRead, useUnreadIncidents, type UnreadIncident } from "./api";

/** Trần lời giải trình — khớp CHECK incident_reads.explanation (migration 0438). */
export const MAX_EXPLANATION = 2000;

const SEVERITY: Record<string, { label: string; tone: string }> = {
  P0: {
    label: "Lỗi nặng (P0)",
    tone: "border-[#e6cdc9] bg-[var(--color-danger-bg)] text-danger-text",
  },
  P1: {
    label: "Lỗi vừa (P1)",
    tone: "border-[#e3d3a8] bg-[var(--color-warning-bg)] text-warning-text",
  },
  P2: { label: "Lỗi nhẹ (P2)", tone: "border-[#c9dbe6] bg-[var(--color-info-bg)] text-info-text" },
};

const KPI_PREFIX = "Điều chỉnh thưởng KPI:";

/**
 * Màn chặn "Tôi đã đọc" — Sổ vi phạm (CEO chốt 3a + 5a 30/09/2026, duyệt mẫu
 * 2a 01/10/2026). Người có phiếu lỗi ĐÃ XÁC NHẬN chưa đọc thì không dùng app
 * tiếp được: không nút đóng, Esc và bấm ra ngoài đều không tắt. Mỗi lần MỘT
 * phiếu; bấm xong máy chủ lưu mốc + lời giải trình (chứng cứ, không sửa lại
 * được) và gửi một lần sửa sang HRM.
 *
 * Gắn ở AppShell cho mọi màn đã đăng nhập. Chỉ hỏi máy chủ khi tài khoản có
 * quyền phúc khảo phiếu của chính mình — đúng tập người cửa này mở cho. Hỏi
 * hỏng thì KHÔNG chặn: khoá người ta ngoài app vì mạng chập chờn tệ hơn trễ
 * một lượt đọc.
 */
export function IncidentReadGate() {
  const allowed = usePermission(ACTION.PerformanceAppealWrite);
  const unread = useUnreadIncidents(allowed);
  const list = unread.data ?? [];
  const [done, setDone] = useState(0);
  const current = list[0];
  if (!current) return null;
  return (
    <IncidentReadDialog
      key={current.id}
      incident={current}
      position={done + 1}
      total={done + list.length}
      onRead={() => setDone((n) => n + 1)}
    />
  );
}

function IncidentReadDialog({
  incident,
  position,
  total,
  onRead,
}: {
  incident: UnreadIncident;
  position: number;
  total: number;
  onRead: () => void;
}) {
  const [explanation, setExplanation] = useState("");
  const mark = useMarkIncidentRead();
  const block = (e: Event) => e.preventDefault();

  const submit = () => {
    mark.mutate({ id: incident.id, explanation: explanation.trim() }, { onSuccess: onRead });
  };

  return (
    <Dialog open>
      <DialogContent
        hideCloseButton
        onEscapeKeyDown={block}
        onPointerDownOutside={block}
        onInteractOutside={block}
        className={cn(
          // Điện thoại: tấm trượt từ đáy, app mờ phía trên (mẫu CEO duyệt).
          "inset-x-0 bottom-0 top-auto flex max-h-[92dvh] flex-col gap-0 overflow-hidden rounded-t-2xl p-0",
          // Máy tính: hộp giữa màn 560px, đầu/chân đứng yên, thân tự cuộn.
          "md:bottom-auto md:max-w-[560px] md:overflow-hidden md:rounded-xl md:p-0",
        )}
      >
        <div className="border-b border-border px-6 pb-3 pt-5">
          <p className="m-0 mb-1 text-[13px] text-text-muted">
            Phiếu {position} / {total} cần đọc
          </p>
          <DialogTitle className="text-xl leading-snug">
            Bạn có một phiếu lỗi đã được xác nhận
          </DialogTitle>
          <DialogDescription className="sr-only">
            Đọc phiếu lỗi rồi bấm Tôi đã đọc để tiếp tục dùng ứng dụng.
          </DialogDescription>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
          <IncidentFacts incident={incident} />

          <label
            htmlFor="incident-read-explanation"
            className="mb-1.5 mt-4 block text-[15px] font-semibold"
          >
            Giải trình của bạn <span className="font-normal text-text-muted">(không bắt buộc)</span>
          </label>
          <Textarea
            id="incident-read-explanation"
            value={explanation}
            maxLength={MAX_EXPLANATION}
            onChange={(e) => setExplanation(e.target.value)}
            placeholder="Nếu muốn, ghi lại hoàn cảnh hoặc ý kiến của bạn…"
            className="min-h-[96px] text-base md:text-base"
          />
          <p className="m-0 mt-1 text-right text-[13px] text-text-muted" aria-live="polite">
            {explanation.length} / {MAX_EXPLANATION} ký tự
          </p>
          {mark.isError ? (
            <p role="alert" className="m-0 mt-2 text-sm text-danger-text">
              {mark.error.message}
            </p>
          ) : null}
        </div>

        <div className="flex justify-end border-t border-border px-6 pb-7 pt-3.5 md:pb-5">
          <Button
            size="lg"
            className="min-h-12 w-full text-base md:w-auto"
            disabled={mark.isPending}
            onClick={submit}
          >
            {mark.isPending ? "Đang ghi nhận…" : "Tôi đã đọc"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Câu nói về phúc khảo, theo hạn THẬT máy chủ gửi (không nói cứng "3 ngày": phiếu
 * xác nhận từ lâu thì hạn đã cạn hoặc sắp cạn, nói sai là người ta bỏ lỡ).
 * `appealDeadline` rỗng = phiếu đã có phúc khảo, mỗi phiếu chỉ một lần.
 */
export function appealSentence(incident: UnreadIncident): string {
  if (!incident.appealDeadline) return "Phiếu này đã có phúc khảo, không gửi thêm được.";
  if (incident.appealExpired) return "Đã hết hạn phúc khảo.";
  const at = new Date(incident.appealDeadline);
  return `Phúc khảo được tới ${format(at, "HH:mm dd/MM")} ở mục Hiệu suất của tôi.`;
}

/** Nội dung phiếu: mức lỗi, tên lỗi, ngày, cơ sở, điều chỉnh thưởng KPI. */
function IncidentFacts({ incident }: { incident: UnreadIncident }) {
  const sev = SEVERITY[incident.severity] ?? { label: incident.severity, tone: "" };
  // Khung đỏ CHỈ khi bảng lương thật sự trừ; câu "được miễn / nhắc nhở" là chữ thường.
  const charged = incident.kpiAdjustPercent != null && incident.chargeNote.startsWith(KPI_PREFIX);
  return (
    <>
      <span
        className={cn(
          "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-sm font-semibold",
          sev.tone,
        )}
      >
        <AlertCircle aria-hidden="true" className="h-4 w-4" />
        {sev.label}
      </span>
      <p className="m-0 mt-2.5 text-[17px] font-semibold leading-snug">{incident.title}</p>
      {incident.code ? (
        <p className="m-0 text-[13px] text-text-muted">
          Mã lỗi {incident.code}
          {incident.codeName ? ` · ${incident.codeName}` : ""}
        </p>
      ) : null}

      <dl className="my-3.5 grid grid-cols-[110px_1fr] gap-x-3 gap-y-2 text-[15px] md:grid-cols-[130px_1fr]">
        <dt className="text-text-muted">Ngày xảy ra</dt>
        <dd className="m-0">{formatOccurredAt(incident.occurredAt)}</dd>
        <dt className="text-text-muted">Cơ sở</dt>
        <dd className="m-0">{incident.branchName || incident.branch}</dd>
        <dt className="text-text-muted">Người xác nhận</dt>
        <dd className="m-0">
          {incident.confirmedBy ? `${incident.confirmedBy} · ` : ""}
          {formatDate(incident.confirmedAt)}
        </dd>
      </dl>

      {charged ? (
        <div className="rounded-lg border border-[#e6cdc9] bg-[var(--color-danger-bg)] px-3 py-2.5 text-sm leading-relaxed text-danger-text">
          <b>{KPI_PREFIX}</b>
          {incident.chargeNote.slice(KPI_PREFIX.length)}
        </div>
      ) : incident.chargeNote ? (
        <p className="m-0 text-sm text-text-muted">{incident.chargeNote}</p>
      ) : null}

      {incident.detail ? (
        <p className="m-0 mt-2.5 whitespace-pre-line rounded-lg bg-bg px-3 py-2.5 text-sm leading-relaxed">
          {incident.detail}
        </p>
      ) : null}

      <p className="m-0 mt-3.5 rounded-lg bg-[var(--color-info-bg)] px-3 py-2.5 text-sm leading-relaxed text-info-text">
        Bấm "Tôi đã đọc" nghĩa là bạn <b>đã nhận được</b> thông báo này — <b>không</b> có nghĩa là
        bạn đồng ý. {appealSentence(incident)}
      </p>
    </>
  );
}
